import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  fetchPriority?: 'high' | 'low' | 'auto';
  loading?: 'lazy' | 'eager';
  usePicture?: boolean;
  webpSrc?: string;
  onLoad?: () => void;
  placeholder?: string;
}

/**
 * OptimizedImage - Improved image component with performance optimizations
 * Uses standard HTML image with best practices for loading
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  fetchPriority,
  loading,
  usePicture = false,
  webpSrc,
  onLoad,
  placeholder,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  // Set defaults based on priority
  const loadingValue = loading || (priority ? 'eager' : 'lazy');
  const fetchPriorityValue = fetchPriority || (priority ? 'high' : 'auto');
  
  // Style to prevent Content Layout Shifts (CLS)
  const dimensionStyles: React.CSSProperties = {};
  if (width) dimensionStyles.width = `${width}px`;
  if (height) dimensionStyles.height = `${height}px`;
  
  // Track image load for diagnostics in development
  useEffect(() => {
    return () => {
      if (process.env.NODE_ENV === 'development' && !isLoaded && !error) {
        console.log(`[Image] Image unmounted before loading: ${src}`);
      }
    };
  }, [src, isLoaded, error]);
  
  // Handle image load completion
  const handleImageLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
    
    // Log image load time in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Image] Loaded: ${src}`);
    }
  };
  
  // Handle image load error
  const handleImageError = () => {
    setError(true);
    console.warn(`[Image] Failed to load: ${src}`);
  };
  
  // Use browser native lazy loading for production
  const imgProps = {
    src,
    alt,
    width,
    height,
    loading: loadingValue,
    fetchPriority: fetchPriorityValue,
    decoding: "async" as const,
    className: cn('max-w-full', isLoaded ? className : cn('opacity-0', className)),
    style: {
      ...dimensionStyles,
      transition: 'opacity 0.2s'
    },
    onLoad: handleImageLoad,
    onError: handleImageError,
    ...props
  };
  
  // If using picture element and webp source is available
  if (usePicture && webpSrc) {
    return (
      <picture>
        {placeholder && !isLoaded && !error && (
          <div className="absolute inset-0 bg-muted animate-pulse" />
        )}
        <source srcSet={webpSrc} type="image/webp" />
        <img {...imgProps} />
      </picture>
    );
  }
  
  // Otherwise use standard img with optimizations
  return (
    <div className={cn('relative', error ? 'bg-muted' : '')}>
      {placeholder && !isLoaded && !error && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      <img {...imgProps} />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
          <span className="text-xs text-muted-foreground">Unable to load image</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(OptimizedImage);
